import React, { useState } from 'react';
import { Trash2, ArrowUpCircle, X } from 'lucide-react';
import { Button, Modal, Select } from '@/shared/components/ui';

interface BulkActionBarProps {
  selectedCount: number;
  onBulkDelete: () => void;
  onBulkPlanChange: () => void;
  onClearSelection: () => void;
  newPlanId: string;
  billingPeriod: string;
  onNewPlanIdChange: (value: string) => void;
  onBillingPeriodChange: (value: string) => void;
  onConfirmBulkAction: () => void;
  onCancelBulkAction: () => void;
  bulkActionType: 'delete' | 'change_plan' | null;
}

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free Starter (Limits Applied)' },
  { value: 'basic', label: 'Basic (5 Clients)' },
  { value: 'pro', label: 'Pro Freelancer (Unlimited)' },
  { value: 'company', label: 'Company (Enterprise)' },
];

const BILLING_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onBulkDelete,
  onBulkPlanChange,
  onClearSelection,
  newPlanId,
  billingPeriod,
  onNewPlanIdChange,
  onBillingPeriodChange,
  onConfirmBulkAction,
  onCancelBulkAction,
  bulkActionType,
}) => {
  return (
    <>
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 flex items-center justify-between gap-4 animate-[slideDown_0.2s_ease-out]">
        <div className="flex items-center gap-3">
          <div className="bg-primary-100 text-primary-700 px-3 py-1.5 rounded-lg font-bold text-sm">
            {selectedCount} user{selectedCount !== 1 ? 's' : ''} selected
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onClearSelection}
            leftIcon={<X size={16} />}
          >
            Clear Selection
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onBulkPlanChange}
            leftIcon={<ArrowUpCircle size={16} />}
          >
            Change Plan
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={onBulkDelete}
            leftIcon={<Trash2 size={16} />}
          >
            Delete Selected
          </Button>
        </div>
      </div>

      {/* Bulk Action Confirmation Modal */}
      {bulkActionType && (
        <Modal
          isOpen={true}
          onClose={onCancelBulkAction}
          title={bulkActionType === 'delete' ? 'Delete Selected Users' : 'Change Plans'}
          size="md"
          footer={
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onCancelBulkAction}>
                Cancel
              </Button>
              <Button
                variant={bulkActionType === 'delete' ? 'danger' : 'primary'}
                onClick={onConfirmBulkAction}
                leftIcon={bulkActionType === 'delete' ? <Trash2 size={18} /> : undefined}
              >
                {bulkActionType === 'delete' ? `Delete ${selectedCount} Users` : 'Update Plans'}
              </Button>
            </div>
          }
        >
          <div className="space-y-5">
            {bulkActionType === 'delete' ? (
              <>
                <p className="text-neutral-600 leading-relaxed">
                  Are you sure you want to permanently delete <strong>{selectedCount} users</strong>? This action cannot be undone and will remove all associated clients, jobs, and invoices for each user.
                </p>
                <div className="bg-error-50 p-4 rounded-xl border border-error-100 flex items-start gap-3">
                  <Trash2 className="text-error-600 mt-0.5" size={20} />
                  <div className="text-sm text-error-800">
                    <p className="font-bold">Warning: Irreversible Action</p>
                    <p className="opacity-80">All data for these users will be permanently removed from the system.</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-5">
                <p className="text-sm text-neutral-600">
                  Update subscription tier and billing cycle for <strong>{selectedCount} selected users</strong>.
                </p>
                <Select
                  label="Package Plan"
                  options={PLAN_OPTIONS}
                  value={newPlanId}
                  onChange={(e) => onNewPlanIdChange(e.target.value)}
                />
                <Select
                  label="Billing Period"
                  options={BILLING_OPTIONS}
                  value={billingPeriod}
                  onChange={(e) => onBillingPeriodChange(e.target.value)}
                />
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};
